// Eş sanal portföyü sayfası (Task 11 — /es).
//
// Üst: özet kart (toplam değer + maliyet + P&L + lot/fon sayıları).
// Orta: lot ekleme formu (collapsible).
// Alt: lot listesi + soft delete.
//
// Veri akışı:
//   /spouse/summary + /spouse/lots Promise.all
//   POST /spouse/lots → state.items başına optimistic ekle (reload da yapar)
//   DELETE /spouse/lots/:id → optimistic remove + hata olursa rollback

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApiError,
  deleteSpouseLot,
  getSpouseLots,
  getSpouseSummary,
  type ApiConfig,
} from "../lib/api";
import type {
  SpouseLotCreateResponse,
  SpouseLotRow,
  SpouseSummary,
} from "../types/api";
import EsLotForm from "../components/EsLotForm";
import EsLotTablosu from "../components/EsLotTablosu";

interface Props {
  cfg: ApiConfig;
}

function fmtTl(n: number, signed = false): string {
  const sign = signed && n > 0 ? "+" : n < 0 ? "−" : "";
  const abs = Math.abs(n).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}${abs} TL`;
}

function fmtPct(n: number): string {
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n * 100).toFixed(2).replace(".", ",");
  return `${sign}%${abs}`;
}

export default function Es({ cfg }: Props) {
  const [summary, setSummary] = useState<SpouseSummary | null>(null);
  const [items, setItems] = useState<SpouseLotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [pendingIds, setPendingIds] = useState<Set<number>>(() => new Set());
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    if (!cfg.base) {
      setErr("API base ayarlanmamış.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const [s, l] = await Promise.all([
        getSpouseSummary(cfg),
        getSpouseLots(cfg),
      ]);
      setSummary(s);
      setItems(l.items);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e);
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreated = useCallback(
    async (_resp: SpouseLotCreateResponse) => {
      // Reload — summary + items'i yeniden çek. Optimistic eklemek yerine
      // tek call: NAV ve current_value alanları backend tarafından dolu gelir.
      await reload();
    },
    [reload],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      // Optimistic remove
      const prev = items;
      const prevSummary = summary;
      setItems((cur) => cur.filter((r) => r.id !== id));
      setPendingIds((s) => {
        const n = new Set(s);
        n.add(id);
        return n;
      });
      try {
        await deleteSpouseLot(cfg, id);
        // Summary'i yeniden çek — total değişti
        const fresh = await getSpouseSummary(cfg);
        setSummary(fresh);
      } catch (e) {
        // Rollback
        setItems(prev);
        setSummary(prevSummary);
        const msg =
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : String(e);
        setErr(`Silinemedi: ${msg}`);
      } finally {
        setPendingIds((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        });
      }
    },
    [cfg, items, summary],
  );

  return (
    <main className="min-h-screen flex flex-col mx-auto" style={{ maxWidth: 430 }}>
      <header className="flex items-center justify-between px-4 pt-3 pb-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Ana sayfa"
            className="text-ink-300 hover:text-ink-100 -ml-2 p-2"
          >
            ‹
          </button>
          <h1 className="text-lg font-semibold tracking-tight text-ink-100">
            Eş Portföyü
          </h1>
          <span className="text-ink-400 text-xs">· sanal</span>
        </div>
      </header>

      {/* Özet kart */}
      <section className="px-4 pb-3">
        <div className="rounded-xl bg-ink-800 border border-ink-700 p-4">
          {loading && !summary ? (
            <p className="text-ink-400 text-sm">Yükleniyor…</p>
          ) : err && !summary ? (
            <div>
              <p className="text-warn-400 text-sm mb-2">Veri çekilemedi</p>
              <p className="text-ink-300 text-xs break-words">{err}</p>
              <button
                type="button"
                onClick={reload}
                className="mt-3 text-xs btn-ghost"
              >
                Tekrar dene
              </button>
            </div>
          ) : summary ? (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
                  Toplam değer
                </span>
                <span className="text-2xl text-ink-100 font-semibold fon-dili">
                  {fmtTl(summary.total_value_tl)}
                </span>
              </div>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-ink-400">Maliyet</span>
                <span className="text-ink-200 fon-dili">
                  {fmtTl(summary.total_cost_tl)}
                </span>
              </div>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-ink-400">Gerçekleşmemiş PnL</span>
                <span
                  className={`fon-dili ${
                    summary.unrealized_pnl_tl >= 0
                      ? "text-gain-400"
                      : "text-loss-400"
                  }`}
                >
                  {fmtTl(summary.unrealized_pnl_tl, true)} ·{" "}
                  {fmtPct(summary.unrealized_pnl_pct)}
                </span>
              </div>
              <div className="flex items-baseline justify-between text-[11px] text-ink-400 pt-1 border-t border-ink-700/60">
                <span>
                  {summary.open_lot_count} açık lot · {summary.fund_count} fon
                  {summary.closed_lot_count > 0 &&
                    ` · ${summary.closed_lot_count} kapalı`}
                </span>
                {summary.funds_missing_nav.length > 0 && (
                  <span
                    className="text-warn-400"
                    title={`NAV yok: ${summary.funds_missing_nav.join(", ")}`}
                  >
                    ⚠ {summary.funds_missing_nav.length} fon NAV yok
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* Form */}
      <section className="px-4 pb-3">
        {!showForm ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="w-full rounded-xl bg-ink-800 border border-dashed border-ink-700 hover:border-accent-500/60 hover:bg-ink-700/40 transition px-4 py-3 text-ink-300 text-sm"
          >
            + Yeni lot ekle
          </button>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
                Yeni lot
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-ink-400 hover:text-ink-200 text-xs"
              >
                Kapat
              </button>
            </div>
            <EsLotForm cfg={cfg} onCreated={handleCreated} />
          </div>
        )}
      </section>

      {/* Lot listesi */}
      <section className="px-4 pb-6">
        <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-1 mb-2">
          Açık lotlar
        </h3>
        {loading && items.length === 0 ? (
          <p className="text-ink-400 text-sm px-1">Yükleniyor…</p>
        ) : (
          <EsLotTablosu
            rows={items}
            onDelete={handleDelete}
            pendingIds={pendingIds}
          />
        )}
      </section>

      <footer className="mt-auto px-4 pb-6 pt-2 text-center text-[11px] text-ink-400">
        AiFon · Eş — sanal portföy · manuel giriş, ayrı vergi mükellefi
      </footer>
    </main>
  );
}
